import { useState } from 'react';
import styled from 'styled-components';
import { Flex, StatusChip } from '@elementary/common-ui/components';
import { colors as themeColors } from '@elementary/common-ui/style/theme';
import { Typography } from '@mui/material';
import { ThresholdLevel } from '../../types';
import { getValueColor } from '../../utils';
import ContentWithLabel from '../shared/ContentWithLabel';
import SegmentedLineChart from '../shared/SegmentedLineChart';
import ScoreFormulaDialog from './ScoreFormulaDialog';

const colors = [
  themeColors.red100,
  themeColors.orange100,
  themeColors.green100,
];

const ValueWrapper = styled(Flex)`
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 20px;
`;

const Value = styled(Typography)<{ $color: string }>`
  font-size: 40px;
  font-weight: 500;
  line-height: 44px;
  color: ${({ $color }) => $color};
`;

type Props = {
  value: number;
  thresholdLevel: ThresholdLevel;
  thresholds: {
    low: number;
    medium: number;
  };
  onTotalHealthScoreFormulaChange: () => void;
};

const TotalHealthScore = ({
  value,
  thresholdLevel,
  thresholds,
  onTotalHealthScoreFormulaChange,
}: Props) => {
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  const numberColor = getValueColor(thresholdLevel);

  return (
    <ContentWithLabel
      variant="big"
      title="Total Health Score"
      openSettings={() => setIsSettingsDialogOpen(true)}
    >
      <ValueWrapper>
        <Value $color={numberColor}>{Math.round(value)}%</Value>

        {thresholdLevel !== null && <StatusChip status={thresholdLevel} />}
      </ValueWrapper>

      <SegmentedLineChart
        segmentsColors={colors}
        segmentsValues={[
          thresholds.low,
          thresholds.medium - thresholds.low,
          100 - thresholds.medium,
        ]}
        markerValue={value}
      />

      {isSettingsDialogOpen && (
        <ScoreFormulaDialog
          isOpen={isSettingsDialogOpen}
          onClose={() => {
            setIsSettingsDialogOpen(false);
          }}
          onSave={() => {
            onTotalHealthScoreFormulaChange();
            setIsSettingsDialogOpen(false);
          }}
        />
      )}
    </ContentWithLabel>
  );
};

export default TotalHealthScore;
